import { useRef } from 'react';

import { NodeViewWrapper } from '@tiptap/react';

import { useResolvedMediaSrc } from '../hooks/useResolvedMediaSrc';

import type { NodeViewProps } from '@tiptap/react';

/**
 * NodeView for an image embedded mid-paragraph/mid-cell (see InlineImage.ts).
 * Rendered as a tiny fixed-size thumbnail inside body text so prose doesn't
 * reflow around whatever the image's natural size happens to be, with a
 * click-to-enlarge preview.
 *
 * Uses the native Popover API rather than `<dialog>.showModal()` -- a modal
 * dialog moves focus into itself, which drags the nearest scroll container
 * (the document viewer) to bring it into view, jumping the reader's scroll
 * position. A popover doesn't force that.
 *
 * `popover="auto"`'s light-dismiss is non-modal by design: clicking outside
 * the popover closes it, but that same click still reaches whatever's
 * underneath (its `::backdrop` doesn't block pointer events, unlike a modal
 * `<dialog>`'s) -- so clicking a different image's trigger while a popover
 * is open would close this one and open the other in the same click. The
 * popover element itself is stretched to cover the full viewport (see
 * styles.css) so it's the only thing hit-tested while open, with the actual
 * visual card as a nested child; clicking the surrounding overlay (not the
 * card) closes it via `handleOverlayClick` below.
 */
export function InlineImageView({ node }: NodeViewProps) {
  const attrs = node.attrs as Record<string, string | null>;
  const href = attrs['data-inline-href'] ?? '';
  const alt = attrs['alt'] ?? 'Image';
  const resolved = useResolvedMediaSrc(href);
  const popoverRef = useRef<HTMLDivElement>(null);

  function handleOverlayClick(event: React.MouseEvent<HTMLDivElement>) {
    if (event.target === event.currentTarget) popoverRef.current?.hidePopover();
  }

  const handle = {
    'data-block-key': attrs['data-block-key'] ?? undefined,
    'data-block-type': attrs['data-block-type'] ?? undefined,
    'data-role': attrs['data-role'] ?? undefined,
  };

  if (resolved.status === 'placeholder') {
    return (
      <NodeViewWrapper as="span">
        <span {...handle} data-part="placeholder">{`[${alt}: image unavailable]`}</span>
      </NodeViewWrapper>
    );
  }

  if (resolved.status === 'loading') {
    return (
      <NodeViewWrapper as="span">
        <span
          {...handle}
          role="status"
          aria-label={`Loading ${alt}`}
          data-part="inline-image-skeleton"
          className="inline-block animate-pulse rounded bg-neutral-200 align-middle"
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span">
      <button
        {...handle}
        type="button"
        data-part="inline-image-trigger"
        aria-label={`Open image: ${alt}`}
        onClick={() => popoverRef.current?.showPopover()}
      >
        <img src={resolved.src} alt={alt} data-part="inline-image" />
      </button>
      <div
        ref={popoverRef}
        popover="auto"
        data-part="inline-image-popover"
        aria-label={alt}
        onClick={handleOverlayClick}
      >
        <div data-part="inline-image-popover-card">
          <button
            type="button"
            data-part="inline-image-popover-close"
            aria-label="Close"
            onClick={() => popoverRef.current?.hidePopover()}
          >
            ×
          </button>
          <img src={resolved.src} alt={alt} data-part="inline-image-popover-image" />
        </div>
      </div>
    </NodeViewWrapper>
  );
}
